import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import useTimeoutEffect from "./useTimeoutEffect";
import { useMeetingStore } from "../../stores/useMeetingStore";
import { useUserStore } from "../../stores/useUserStore";
import useGetMeeting from "../api/useGetMeeting";
import useMeetingChangeSocket from "../api/useMeetingChangeSocket";
import useShowError from "./useShowError";
import { toString } from "../../utilities/stringify";
import useAxios from "../api/useAxios";
import logExit from "../../utilities/exitLogger";

// localStorage 键定义
const PARTICIPANT_INFO_PREFIX = "participant_";
const PARTICIPANT_NAME_KEY = "participant_name";
const PARTICIPANT_GROUP_KEY = "participant_group";

export interface StoredParticipantInfo {
    name: string;
    group: string;
    joinedAt: number;
}

/**
 * 保存参与者信息到localStorage
 */
export const saveParticipantInfo = (meetingId: string, name: string, group: string) => {
    const info: StoredParticipantInfo = {
        name,
        group,
        joinedAt: Date.now(),
    };
    localStorage.setItem(`${PARTICIPANT_INFO_PREFIX}${meetingId}`, JSON.stringify(info));
    console.log(`[Meeting] Saved participant info for meeting ${meetingId}:`, info);
};

/**
 * 从localStorage读取参与者信息
 */
export const getStoredParticipantInfo = (meetingId: string): StoredParticipantInfo | null => {
    const stored = localStorage.getItem(`${PARTICIPANT_INFO_PREFIX}${meetingId}`);
    if (!stored) return null;
    
    try {
        return JSON.parse(stored);
    } catch {
        console.warn(`Failed to parse stored participant info for meeting ${meetingId}`);
        return null;
    }
};

/**
 * 清除参与者信息
 */
export const clearParticipantInfo = (meetingId: string) => {
    localStorage.removeItem(`${PARTICIPANT_INFO_PREFIX}${meetingId}`);
    console.log(`[Meeting] Cleared participant info for meeting ${meetingId}`);
};

const useMeetingFromParams = () => {
    const navigate = useNavigate();
    const meetingId = (useParams() as { id: string }).id;

    const { setMeeting, meeting } = useMeetingStore();
    const user = useUserStore((state) => state.user);
    const { get: getApi } = useAxios();

    /**
     * 处理会议结束事件
     */
    const handleMeetingEnded = () => {
        console.log(`[Meeting] Meeting ${meetingId} has ended, clearing participant info and navigating to home`);
        // 监控：会议结束导致全体离开（host 离开 / 会议被移除时会触发）
        logExit("meeting-ended", { meetingId });
        clearParticipantInfo(meetingId);
        // 清除会议状态
        setMeeting(undefined);
        // 返回首页
        navigate("/");
    };

    useMeetingChangeSocket({ 
        meetingId,
        onReconnected: async () => {
            // SignalR重连后，自动恢复参与者状态
            await recoverParticipantStateAfterReconnect(meetingId);
        },
        onMeetingEnded: handleMeetingEnded
    });

    const { getMeeting, loading, error, clearError } = useGetMeeting();

    useShowError(error, clearError);

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const getMeetingWithRetry = async (
        id: string,
        maxAttempts: number = 3,
        delayMs: number = 350
    ) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const loadedMeeting = await getMeeting(id);
            if (loadedMeeting) {
                if (attempt > 1) {
                    console.log(`[Meeting] Meeting loaded after retry ${attempt}/${maxAttempts}`);
                }
                return loadedMeeting;
            }

            if (attempt < maxAttempts) {
                console.warn(
                    `[Meeting] FindMeeting returned empty (attempt ${attempt}/${maxAttempts}), retrying in ${delayMs}ms...`
                );
                await wait(delayMs);
            }
        }

        return undefined;
    };

    /**
     * 重连后恢复参与者状态
     */
    const recoverParticipantStateAfterReconnect = async (mId: string) => {
        const storedParticipant = getStoredParticipantInfo(mId);
        
        if (!storedParticipant) {
            console.log(`[Meeting] No stored participant info found for meeting ${mId}, skipping recovery`);
            return;
        }

        console.log(`[Meeting] Recovering participant state for meeting ${mId}:`, storedParticipant);

        try {
            // 调用Join API恢复参与者状态
            const response = await getApi<any>(
                `/api/meeting/Join?meetingId=${mId}&userName=${encodeURIComponent(storedParticipant.name)}&group=${encodeURIComponent(storedParticipant.group)}`
            );
            
            if (response) {
                console.log(`[Meeting] Successfully recovered participant state after reconnection`);
                setMeeting(response);
            }
        } catch (error) {
            console.error(`[Meeting] Failed to recover participant state:`, error);
            // 如果恢复失败，清除存储的信息
            clearParticipantInfo(mId);
        }
    };

    useTimeoutEffect(
        () => {
            if (meetingId.length === 0) {
                console.warn("Meeting ID is empty, navigating to home");
                logExit("meeting-load-failed", { error: "empty meetingId" });
                navigate("/");
            }
            else if (meeting?.id !== meetingId) {
                (async () => {
                    console.log(`Loading meeting: ${meetingId}`);
                    let loadedMeeting = await getMeetingWithRetry(meetingId);
                    if (!loadedMeeting) {
                        console.warn(`Meeting ${meetingId} not found after retries, navigating to home`);
                        // 监控：会议在多次重试后仍加载失败，被动跳回首页
                        logExit("meeting-load-failed", {
                            meetingId,
                            error: "getMeeting returned empty after retries",
                        });
                        return navigate("/");
                    }
                    console.log(`Meeting ${meetingId} loaded successfully`);
                    setMeeting(loadedMeeting);
                })();
            }
        },
        [user.id],
        1000  
    );

    return loading;
};

export default useMeetingFromParams;
